package com.yendo.mx.ui.home

import androidx.compose.animation.*
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import coil3.compose.AsyncImage
import coil3.compose.LocalPlatformContext
import coil3.request.ImageRequest
import coil3.request.crossfade
import com.yendo.mx.utils.rememberYendoImagePickerLauncher
import com.yendo.mx.ui.theme.RosaYendo
import com.yendo.mx.ui.cart.OpcionesPagoEfectivo
import com.yendo.mx.ui.cart.MetodoPagoItem
import com.yendo.mx.ui.cart.BotonSeleccion
import com.yendo.mx.ui.cart.ChipSeleccion
import com.yendo.mx.repository.AuthRepository
import com.yendo.mx.repository.ConfigGlobalRepository
import com.yendo.mx.utils.ConfigManager
import com.yendo.mx.utils.formatPrecio
import com.yendo.mx.utils.BackHandler
import com.yendo.mx.utils.LogisticaUtils
import com.yendo.mx.utils.MediaPermissionManager
import com.yendo.mx.utils.rememberMediaPermissionManager
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.launch
import kotlin.time.Duration.Companion.days
import org.jetbrains.compose.resources.ExperimentalResourceApi
import yendo.shared.generated.resources.*

import androidx.lifecycle.viewmodel.compose.viewModel
import androidx.lifecycle.createSavedStateHandle

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun VistaEnvios(
    vm: EnviosViewModel = viewModel { EnviosViewModel(createSavedStateHandle()) },
    onBack: () -> Unit,
    onSeleccionarUbicacion: (isOrigen: Boolean) -> Unit,
    onPedidoConfirmado: (String) -> Unit
) {
    var pasoActual by rememberSaveable { mutableIntStateOf(1) }
    val scope = rememberCoroutineScope()
    val uriHandler = LocalUriHandler.current

    var mostrarOpcionesFoto by remember { mutableStateOf(false) }
    var esFotoPaquete by remember { mutableStateOf(false) } // Para saber qué estamos capturando

    // 🚀 BLINDAJE: NUNCA usar rememberSaveable con ByteArray de fotos.
    var imagenBytesComprobante by remember { mutableStateOf<ByteArray?>(null) }
    
    val imagePicker = rememberYendoImagePickerLauncher(
        onResult = { byteArrays ->
            val result = byteArrays.firstOrNull()
            if (esFotoPaquete) {
                vm.fotoPaqueteBytes = result
            } else {
                imagenBytesComprobante = result
            }
        }
    )

    val permissionManager = rememberMediaPermissionManager { result ->
        if (esFotoPaquete) {
            vm.fotoPaqueteBytes = result
        }
    }

    val usuarioId = AuthRepository.usuarioActual.collectAsState().value?.uid
    val tarjetasGuardadas = remember { mutableStateListOf<com.yendo.mx.ui.profile.TarjetaGuardada>() }
    var tarjetaSeleccionada by remember { mutableStateOf<com.yendo.mx.ui.profile.TarjetaGuardada?>(null) }
    var mostrarSelectorTarjetas by rememberSaveable { mutableStateOf(false) }
    var mostrarSheetTransferencia by rememberSaveable { mutableStateOf(false) }
    var mostrarDialogoAgregarTarjeta by rememberSaveable { mutableStateOf(false) }

    LaunchedEffect(usuarioId) {
        if (usuarioId != null) {
            Firebase.firestore.collection("Usuarios").document(usuarioId).collection("Tarjetas")
                .get().let { snapshot ->
                    val tarjetas = snapshot.documents.map { it.data<com.yendo.mx.ui.profile.TarjetaGuardada>().copy(id = it.id) }
                    tarjetasGuardadas.addAll(tarjetas)
                    if (tarjetas.isNotEmpty()) tarjetaSeleccionada = tarjetas.first()
                }
        }
    }

    BackHandler(enabled = pasoActual == 2) { pasoActual = 1 }

    val isDark = isSystemInDarkTheme()
    val colorFondoGlobal = remember(isDark) { if (isDark) Color(0xFF121212) else Color(0xFFF8F8F8) }
    val colorCard = remember(isDark) { if (isDark) Color(0xFF1E1E1E) else Color.White }
    val colorTextoPrincipal = remember(isDark) { if (isDark) Color.White else Color.Black }
    val colorTextoSecundario = remember(isDark) { if (isDark) Color(0xFFAAAAAA) else Color.Gray }

    // 🚀 OPTIMIZACIÓN LISP / COMPOSE: Memoria reactiva blindada para 120 FPS
    val validadoInfo by remember(
        vm.origen, vm.destino, vm.indicacionesOrigen, vm.indicacionesDestino,
        vm.nombreContactoContacto, vm.telefonoContacto, vm.metodoPago,
        imagenBytesComprobante, tarjetaSeleccionada, vm.precioCotizado, vm.totalPagar
    ) {
        derivedStateOf {
            val telDigits = vm.telefonoContacto.filter { it.isDigit() }
            val telValido = telDigits.length == 10
            val esTransferSinComprobante = vm.metodoPago == "Transferencia" && imagenBytesComprobante == null
            val esTarjetaSinSeleccionar = vm.metodoPago == "Tarjeta" && tarjetaSeleccionada == null

            val ok = vm.origen != null && vm.destino != null &&
                    vm.indicacionesOrigen.isNotBlank() && vm.indicacionesDestino.isNotBlank() &&
                    vm.nombreContactoContacto.isNotBlank() && telValido &&
                    !esTransferSinComprobante && !esTarjetaSinSeleccionar

            val totalTexto = if (vm.precioCotizado > 0) " • $${formatPrecio(vm.totalPagar)} MXN" else ""

            val msg = when {
                vm.origen == null || vm.destino == null -> "Selecciona origen y destino"
                vm.indicacionesOrigen.isBlank() || vm.indicacionesDestino.isBlank() -> "Agrega las referencias *"
                vm.nombreContactoContacto.isBlank() -> "Ingresa el nombre del contacto"
                vm.telefonoContacto.isBlank() -> "Ingresa el teléfono del contacto"
                !telValido -> "El teléfono debe ser de 10 dígitos (${telDigits.length}/10)"
                esTransferSinComprobante -> "Adjunta tu comprobante SPEI *"
                esTarjetaSinSeleccionar -> "Selecciona una tarjeta *"
                else -> "Confirmar Envío$totalTexto"
            }
            Pair(ok, msg)
        }
    }
    val validado = validadoInfo.first
    val mensajeBoton = validadoInfo.second

    // 🚀 UNLOCK PROGRESIVO: Solo habilita la sección de pago y desglose cuando Punto A y Punto B están seleccionados
    val ubicacionesListas by remember(vm.origen, vm.destino) {
        derivedStateOf { vm.origen != null && vm.destino != null }
    }

    val enviosPausados = ConfigManager.configActiva?.logistica?.envios_pausados ?: false
    val plataformaFueraHorario = !LogisticaUtils.estaPlataformaAbierta(ConfigManager.configActiva)

    val mensajeAlerta = when {
        enviosPausados -> "Envíos está pausada temporalmente en tu zona. Por favor, intenta más tarde."
        plataformaFueraHorario -> "YENDO está cerrado en este momento. Consulta los horarios de operación."
        else -> null
    }

    if (mensajeAlerta != null) {
        EmergencyBlockedScreen(
            mensaje = mensajeAlerta,
            colorTextoPrincipal = colorTextoPrincipal,
            colorTextoSecundario = colorTextoSecundario,
            onBack = onBack
        )
        return
    }

    val focusManager = LocalFocusManager.current

    Scaffold(
        containerColor = colorFondoGlobal,
        bottomBar = {
            Surface(shadowElevation = 12.dp, color = colorCard) {
                Column(Modifier.padding(16.dp).navigationBarsPadding(), verticalArrangement = Arrangement.spacedBy(10.dp)) {
                    Button(
                        onClick = {
                            vm.confirmarEnvio(
                                cardIdPersistente = tarjetaSeleccionada?.id,
                                tokenTemporal = tarjetaSeleccionada?.tokenPago,
                                issuerId = tarjetaSeleccionada?.issuerId,
                                comprobanteBytes = imagenBytesComprobante
                            )
                        },
                        modifier = Modifier.fillMaxWidth().height(54.dp),
                        enabled = validado && !vm.cargando,
                        colors = ButtonDefaults.buttonColors(containerColor = RosaYendo, disabledContainerColor = if (isDark) Color(0xFF333333) else Color(0xFFE0E0E0)),
                        shape = RoundedCornerShape(14.dp)
                    ) {
                        if (vm.cargando) {
                            CircularProgressIndicator(color = Color.White, modifier = Modifier.size(24.dp))
                        } else {
                            Text(text = mensajeBoton, fontWeight = FontWeight.ExtraBold, fontSize = 16.sp, color = if(validado) Color.White else colorTextoSecundario)
                        }
                    }
                }
            }
        }
    ) { padding ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .pointerInput(Unit) {
                    detectTapGestures(onTap = { focusManager.clearFocus() })
                }
        ) {
            LazyColumn(
                modifier = Modifier.fillMaxSize().padding(horizontal = 16.dp),
                contentPadding = PaddingValues(top = 16.dp, bottom = padding.calculateBottomPadding() + 32.dp),
                verticalArrangement = Arrangement.spacedBy(20.dp)
            ) {
                    item(key = "titulo_envios", contentType = "header") {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .statusBarsPadding()
                                .padding(vertical = 12.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            IconButton(
                                onClick = onBack,
                                modifier = Modifier
                                    .size(42.dp)
                                    .background(if (isDark) Color(0xFF2C2C2E) else Color(0xFFEFEFEF), CircleShape)
                            ) {
                                Icon(
                                    Icons.AutoMirrored.Filled.ArrowBack,
                                    contentDescription = "Volver",
                                    tint = colorTextoPrincipal,
                                    modifier = Modifier.size(20.dp)
                                )
                            }
                            Spacer(modifier = Modifier.width(16.dp))
                            Text(
                                "Envíos P2P 📦",
                                fontWeight = FontWeight.Black,
                                fontSize = 26.sp,
                                color = colorTextoPrincipal
                            )
                        }
                    }

                    // 🚀 1. TOGGLE ENVIANDO / RECIBIENDO
                    item(key = "toggle_rol", contentType = "toggle") {
                                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                    OutlinedButton(
                                        onClick = { vm.updateRolEnvio("ENVIANDO") },
                                        modifier = Modifier.weight(1f).height(48.dp),
                                        colors = ButtonDefaults.outlinedButtonColors(
                                            containerColor = if (vm.rolEnvio == "ENVIANDO") RosaYendo.copy(alpha = 0.1f) else Color.Transparent,
                                            contentColor = if (vm.rolEnvio == "ENVIANDO") RosaYendo else colorTextoSecundario
                                        ),
                                        border = BorderStroke(if (vm.rolEnvio == "ENVIANDO") 2.dp else 1.dp, if (vm.rolEnvio == "ENVIANDO") RosaYendo else colorTextoSecundario.copy(alpha = 0.3f)),
                                        shape = RoundedCornerShape(12.dp)
                                    ) { Text("Estoy enviando", fontWeight = FontWeight.Bold) }

                                    OutlinedButton(
                                        onClick = { vm.updateRolEnvio("RECIBIENDO") },
                                        modifier = Modifier.weight(1f).height(48.dp),
                                        colors = ButtonDefaults.outlinedButtonColors(
                                            containerColor = if (vm.rolEnvio == "RECIBIENDO") RosaYendo.copy(alpha = 0.1f) else Color.Transparent,
                                            contentColor = if (vm.rolEnvio == "RECIBIENDO") RosaYendo else colorTextoSecundario
                                        ),
                                        border = BorderStroke(if (vm.rolEnvio == "RECIBIENDO") 2.dp else 1.dp, if (vm.rolEnvio == "RECIBIENDO") RosaYendo else colorTextoSecundario.copy(alpha = 0.3f)),
                                        shape = RoundedCornerShape(12.dp)
                                    ) { Text("Voy a recibir", fontWeight = FontWeight.Bold) }
                                }
                            }

                            // 🚀 2. LOGÍSTICA
                            item(key = "logistica_card", contentType = "card") {
                                Card(elevation = CardDefaults.cardElevation(2.dp), colors = CardDefaults.cardColors(containerColor = colorCard), shape = RoundedCornerShape(16.dp)) {
                                    Column(Modifier.padding(16.dp)) {
                                        val tituloA = if(vm.rolEnvio == "ENVIANDO") "Punto A: Recoger aquí..." else "Punto A: Recoger con..."
                                        UbicacionRow(if(vm.rolEnvio == "ENVIANDO") Icons.Default.MyLocation else Icons.Default.Place, tituloA, vm.origen?.direccion ?: "Seleccionar origen", RosaYendo, colorTextoPrincipal) { onSeleccionarUbicacion(true) }
                                        if (vm.origen != null) {
                                            val faltaOrigen = vm.indicacionesOrigen.isBlank()
                                            OutlinedTextField(
                                                value = vm.indicacionesOrigen, onValueChange = { vm.updateIndicacionesOrigen(it) },
                                                label = { Text("Referencias de recolección *", fontWeight = FontWeight.Bold, color = if(faltaOrigen) Color.Red else RosaYendo) },
                                                placeholder = { Text("Ej. Casa azul, entre calles X y Y...", fontSize = 12.sp) },
                                                modifier = Modifier.fillMaxWidth().padding(top = 8.dp),
                                                minLines = 2, textStyle = LocalTextStyle.current.copy(color = colorTextoPrincipal),
                                                colors = OutlinedTextFieldDefaults.colors(
                                                    focusedTextColor = colorTextoPrincipal, unfocusedTextColor = colorTextoPrincipal, cursorColor = RosaYendo,
                                                    focusedBorderColor = RosaYendo, unfocusedBorderColor = if(faltaOrigen) Color.Red.copy(alpha = 0.5f) else colorTextoSecundario.copy(alpha = 0.3f)
                                                )
                                            )
                                        }

                                        HorizontalDivider(Modifier.padding(vertical = 12.dp), color = colorTextoSecundario.copy(alpha = 0.1f))

                                        val tituloB = if(vm.rolEnvio == "ENVIANDO") "Punto B: Entregar con..." else "Punto B: Entregar aquí..."
                                        UbicacionRow(if(vm.rolEnvio == "ENVIANDO") Icons.Default.Place else Icons.Default.MyLocation, tituloB, vm.destino?.direccion ?: "Seleccionar destino", colorTextoSecundario, colorTextoPrincipal) { onSeleccionarUbicacion(false) }
                                        if (vm.destino != null) {
                                            val faltaDestino = vm.indicacionesDestino.isBlank()
                                            OutlinedTextField(
                                                value = vm.indicacionesDestino, onValueChange = { vm.updateIndicacionesDestino(it) },
                                                label = { Text("Referencias de entrega *", fontWeight = FontWeight.Bold, color = if(faltaDestino) Color.Red else RosaYendo) },
                                                placeholder = { Text("Ej. Zaguán negro, tocar fuerte el timbre...", fontSize = 12.sp) },
                                                modifier = Modifier.fillMaxWidth().padding(top = 8.dp),
                                                minLines = 2, textStyle = LocalTextStyle.current.copy(color = colorTextoPrincipal),
                                                colors = OutlinedTextFieldDefaults.colors(
                                                    focusedTextColor = colorTextoPrincipal, unfocusedTextColor = colorTextoPrincipal, cursorColor = RosaYendo,
                                                    focusedBorderColor = RosaYendo, unfocusedBorderColor = if(faltaDestino) Color.Red.copy(alpha = 0.5f) else colorTextoSecundario.copy(alpha = 0.3f)
                                                )
                                            )
                                        }
                                    }
                                }
                            }

                            // 🚀 3. CONTACTO DINÁMICO
                            item(key = "contacto_dinamico", contentType = "inputs") {
                                val tituloContacto = if (vm.rolEnvio == "ENVIANDO") "¿Quién va a recibir el paquete?" else "¿Quién te envía el paquete?"
                                Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                                    Text(tituloContacto, fontWeight = FontWeight.Bold, fontSize = 16.sp, color = colorTextoPrincipal)
                                    OutlinedTextField(
                                        value = vm.nombreContactoContacto, onValueChange = { vm.updateNombreContactoContacto(it) }, label = { Text("Nombre de la persona *") },
                                        modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(12.dp), textStyle = LocalTextStyle.current.copy(color = colorTextoPrincipal),
                                        colors = OutlinedTextFieldDefaults.colors(focusedTextColor = colorTextoPrincipal, unfocusedTextColor = colorTextoPrincipal, cursorColor = RosaYendo, focusedBorderColor = RosaYendo)
                                    )
                                    OutlinedTextField(
                                        value = vm.telefonoContacto,
                                        onValueChange = { if (it.length <= 10 && it.all { c -> c.isDigit() }) vm.updateTelefonoContacto(it) },
                                        label = { Text("Teléfono de la persona *") },
                                        modifier = Modifier.fillMaxWidth(), keyboardOptions = androidx.compose.foundation.text.KeyboardOptions(keyboardType = KeyboardType.Phone),
                                        shape = RoundedCornerShape(12.dp), textStyle = LocalTextStyle.current.copy(color = colorTextoPrincipal),
                                        prefix = { Text("+52 ", color = colorTextoPrincipal, fontWeight = FontWeight.Bold) },
                                        colors = OutlinedTextFieldDefaults.colors(focusedTextColor = colorTextoPrincipal, unfocusedTextColor = colorTextoPrincipal, cursorColor = RosaYendo, focusedBorderColor = RosaYendo)
                                    )
                                }
                            }

                            // 🚀 4. DETALLES DEL PAQUETE
                            item(key = "detalles_paquete", contentType = "inputs") {
                                Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                                    val tituloDetallesPaquete = if (vm.rolEnvio == "ENVIANDO") "¿Qué vas a enviar?" else "¿Qué vas a recibir?"
                                    Text(tituloDetallesPaquete, fontWeight = FontWeight.ExtraBold, fontSize = 16.sp, color = colorTextoPrincipal)

                                    val categoriasEnvio = vm.tiposPaquete.ifEmpty { listOf("Documentos", "Ropa", "Llaves", "Comida", "Otro") }
                                    LazyRow(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                        items(categoriasEnvio) { cat ->
                                            FilterChip(
                                                selected = vm.categoriaPaquete == cat,
                                                onClick = { vm.categoriaPaquete = cat },
                                                label = { Text(cat) },
                                                colors = FilterChipDefaults.filterChipColors(
                                                    selectedContainerColor = RosaYendo,
                                                    selectedLabelColor = Color.White
                                                )
                                            )
                                        }
                                    }

                                    OutlinedTextField(
                                        value = vm.descripcionPaquete,
                                        onValueChange = { vm.descripcionPaquete = it },
                                        modifier = Modifier.fillMaxWidth(),
                                        placeholder = { Text("Ej. Caja de zapatos azul, frágil...") },
                                        shape = RoundedCornerShape(12.dp),
                                        textStyle = LocalTextStyle.current.copy(color = colorTextoPrincipal),
                                        colors = OutlinedTextFieldDefaults.colors(focusedTextColor = colorTextoPrincipal, unfocusedTextColor = colorTextoPrincipal, cursorColor = RosaYendo, focusedBorderColor = RosaYendo)
                                    )

                                    if (vm.rolEnvio == "ENVIANDO") {
                                        Spacer(Modifier.height(4.dp))

                                        Text("Foto del paquete (Recomendado)", fontWeight = FontWeight.Bold, fontSize = 14.sp, color = colorTextoPrincipal)
                                        Text("Ayuda al repartidor a identificar tu envío y te protege.", fontSize = 12.sp, color = colorTextoSecundario)

                                        Box(
                                            modifier = Modifier
                                                .fillMaxWidth()
                                                .height(120.dp)
                                                .clip(RoundedCornerShape(12.dp))
                                                .background(if(isDark) Color(0xFF1E1E1E) else Color(0xFFF5F5F5))
                                                .clickable { mostrarOpcionesFoto = true },
                                            contentAlignment = Alignment.Center
                                        ) {
                                            if (vm.fotoPaqueteBytes != null) {
                                                AsyncImage(
                                                    model = vm.fotoPaqueteBytes,
                                                    contentDescription = "Foto paquete",
                                                    contentScale = ContentScale.Crop,
                                                    modifier = Modifier.fillMaxSize()
                                                )
                                            } else {
                                                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                                                    Icon(Icons.Default.CameraAlt, null, tint = RosaYendo, modifier = Modifier.size(32.dp))
                                                    Text("Tocar para agregar foto", color = RosaYendo, fontWeight = FontWeight.Bold)
                                                }
                                            }
                                        }
                                    }

                                    // 🚀 SWITCH DE SEGURIDAD (PIN)
                                    Row(
                                        modifier = Modifier.fillMaxWidth().padding(vertical = 8.dp),
                                        verticalAlignment = Alignment.CenterVertically,
                                        horizontalArrangement = Arrangement.SpaceBetween
                                    ) {
                                        Column(modifier = Modifier.weight(1f)) {
                                            Text("Requerir PIN de entrega", fontWeight = FontWeight.Bold, color = colorTextoPrincipal)
                                            Text("El repartidor te pedirá un código de 4 dígitos al entregar.", fontSize = 11.sp, color = colorTextoSecundario)
                                        }
                                        Switch(
                                            checked = vm.requierePin,
                                            onCheckedChange = { vm.updateRequierePin(it) },
                                            colors = SwitchDefaults.colors(checkedThumbColor = RosaYendo, checkedTrackColor = RosaYendo.copy(alpha = 0.5f))
                                        )
                                    }
                                }
                            }

                            // 🚀 ¿En qué lo llevamos?

                            item {
                                Spacer(Modifier.height(12.dp))
                                Text("¿En qué lo llevamos?", fontWeight = FontWeight.Bold, fontSize = 18.sp, color = colorTextoPrincipal)
                                Spacer(Modifier.height(12.dp))
                                Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                                    vm.vehiculos.forEach { v ->
                                        val isMoto = v.contains("Moto", ignoreCase = true)
                                        val isSelected = vm.vehiculoSelected == v
                                        val subtitulo = if (isMoto) "Cabe en mochila • Máx. 40x40x40 cm • Hasta 10 kg" else "Cabe en cajuela • Máx. 120x80x60 cm • Hasta 20 kg"

                                        Card(
                                            onClick = { vm.updateVehiculoSelected(v); vm.calcularCotizacion() },
                                            modifier = Modifier.fillMaxWidth(),
                                            colors = CardDefaults.cardColors(containerColor = if (isSelected) RosaYendo.copy(alpha = 0.1f) else colorCard),
                                            border = BorderStroke(if (isSelected) 2.dp else 1.dp, if (isSelected) RosaYendo else colorTextoSecundario.copy(alpha = 0.3f)),
                                            shape = RoundedCornerShape(16.dp)
                                        ) {
                                            Row(modifier = Modifier.padding(16.dp), verticalAlignment = Alignment.CenterVertically) {
                                                Icon(if (isMoto) Icons.Default.TwoWheeler else Icons.Default.DirectionsCar, null, tint = if (isSelected) RosaYendo else colorTextoSecundario, modifier = Modifier.size(32.dp))
                                                Spacer(Modifier.width(16.dp))
                                                Column(modifier = Modifier.weight(1f)) {
                                                    Text(v, color = colorTextoPrincipal, fontWeight = FontWeight.Bold, fontSize = 16.sp)
                                                    Text(subtitulo, color = colorTextoSecundario, fontSize = 12.sp, lineHeight = 16.sp, modifier = Modifier.padding(top = 4.dp))
                                                }
                                            }
                                        }
                                    }
                                }
                            }

                            item {
                                Spacer(Modifier.height(12.dp))
                                Text("¿Cuándo lo necesitas?", fontWeight = FontWeight.Bold, fontSize = 18.sp, color = colorTextoPrincipal)
                                Spacer(Modifier.height(12.dp))

                                val permiteProg = ConfigManager.configActiva?.logistica?.envios_programados_activos ?: true

                                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                                    BotonSeleccion("Lo antes posible", Icons.Default.Bolt, vm.tipoTiempo == "ASAP", { vm.updateTipoTiempo("ASAP") }, Modifier.weight(1f), isDark)
                                    if (permiteProg) {
                                        BotonSeleccion("Programar", Icons.Default.CalendarToday, vm.tipoTiempo == "Programado", { vm.updateTipoTiempo("Programado") }, Modifier.weight(1f), isDark)
                                    }
                                }

                                AnimatedVisibility(visible = vm.tipoTiempo == "Programado") {
                                    Card(modifier = Modifier.fillMaxWidth().padding(top = 16.dp), colors = CardDefaults.cardColors(containerColor = colorCard), border = BorderStroke(1.dp, colorTextoSecundario.copy(alpha = 0.2f))) {
                                        Column(modifier = Modifier.padding(16.dp)) {
                                            val horasDisponibles = vm.obtenerHorariosDisponibles(vm.diaProgramado == "Mañana")

                                            Text("Día", fontWeight = FontWeight.Bold, fontSize = 13.sp, color = colorTextoSecundario)
                                            Row(modifier = Modifier.fillMaxWidth().padding(vertical = 8.dp), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                                ChipSeleccion("Hoy", vm.diaProgramado == "Hoy", isDark) { vm.updateDiaProgramado("Hoy") }
                                                ChipSeleccion("Mañana", vm.diaProgramado == "Mañana", isDark) { vm.updateDiaProgramado("Mañana") }
                                            }
                                            Spacer(Modifier.height(8.dp))
                                            Text("Hora estimada", fontWeight = FontWeight.Bold, fontSize = 13.sp, color = colorTextoSecundario)

                                            if (horasDisponibles.isEmpty() && vm.diaProgramado == "Hoy") {
                                                Text("Ya no hay horarios hoy. Selecciona mañana.", color = Color.Red, fontSize = 13.sp, modifier = Modifier.padding(vertical = 8.dp))
                                            } else {
                                                Row(modifier = Modifier.fillMaxWidth().padding(vertical = 8.dp).horizontalScroll(rememberScrollState()), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                                    horasDisponibles.forEach { ChipSeleccion(it, vm.horaProgramada == it, isDark) { vm.updateHoraProgramada(it) } }
                                                }
                                            }
                                        }
                                    }
                                }
                            }

                            if (ubicacionesListas) {
                                // 🚀 8. DESGLOSE FINANCIERO DEL ENVÍO
                                item {
                                Card(
                                    modifier = Modifier.fillMaxWidth(),
                                    colors = CardDefaults.cardColors(containerColor = colorCard),
                                    shape = RoundedCornerShape(16.dp),
                                    border = BorderStroke(1.dp, colorTextoSecundario.copy(alpha = 0.2f))
                                ) {
                                    Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(10.dp)) {
                                        Row(
                                            modifier = Modifier.fillMaxWidth(),
                                            horizontalArrangement = Arrangement.SpaceBetween,
                                            verticalAlignment = Alignment.CenterVertically
                                        ) {
                                            Text("Resumen de la tarifa", fontWeight = FontWeight.ExtraBold, fontSize = 16.sp, color = colorTextoPrincipal)
                                            if (vm.distanciaSimulada > 0) {
                                                Text("Distancia: ${vm.distanciaSimulada} km", fontSize = 12.sp, color = RosaYendo, fontWeight = FontWeight.Bold)
                                            }
                                        }

                                        HorizontalDivider(color = colorTextoSecundario.copy(alpha = 0.1f))

                                        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                                            Text("Costo del envío", fontSize = 13.sp, color = colorTextoSecundario)
                                            Text("$${formatPrecio(vm.costoEnvioPuro)} MXN", fontSize = 13.sp, color = colorTextoPrincipal, fontWeight = FontWeight.Bold)
                                        }

                                        if (vm.tarifaServicioApp > 0) {
                                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                                                Text("Tarifa de servicio", fontSize = 13.sp, color = colorTextoSecundario)
                                                Text("$${formatPrecio(vm.tarifaServicioApp)} MXN", fontSize = 13.sp, color = colorTextoPrincipal, fontWeight = FontWeight.Bold)
                                            }
                                        }

                                        if (vm.descuentoAplicado > 0) {
                                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                                                Text("Descuento aplicado", fontSize = 13.sp, color = Color(0xFF00C853))
                                                Text("-$${formatPrecio(vm.descuentoAplicado)} MXN", fontSize = 13.sp, color = Color(0xFF00C853), fontWeight = FontWeight.Bold)
                                            }
                                        }

                                        HorizontalDivider(color = colorTextoSecundario.copy(alpha = 0.15f))

                                        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                                            Text("Total estimado", fontWeight = FontWeight.Black, fontSize = 16.sp, color = colorTextoPrincipal)
                                            Text("$${formatPrecio(vm.totalPagar)} MXN", fontWeight = FontWeight.Black, fontSize = 20.sp, color = RosaYendo)
                                        }
                                    }
                                }
                            }

                            item {
                                Spacer(Modifier.height(12.dp))
                                val configZona = ConfigManager.configActiva?.finanzas_cliente
                                val metodosPermitidos = configZona?.mandaditos_metodos_pago_activos?.takeIf { it.isNotEmpty() }
                                    ?: configZona?.metodos_pago_activos
                                    ?: listOf("Efectivo", "Tarjeta", "Transferencia")

                                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                    if(metodosPermitidos.contains("Efectivo")) MetodoPagoItem("Efectivo", Icons.Default.Payments, vm.metodoPago == "Efectivo", Modifier.weight(1f), isDark) { vm.updateMetodoPago("Efectivo") }
                                    if(metodosPermitidos.contains("Tarjeta")) {
                                        val textoTarjeta = if (tarjetaSeleccionada != null) "••• ${tarjetaSeleccionada!!.terminacion}" else "Tarjeta"
                                        MetodoPagoItem(textoTarjeta, Icons.Default.CreditCard, vm.metodoPago == "Tarjeta", Modifier.weight(1f), isDark) {
                                            vm.updateMetodoPago("Tarjeta")
                                            mostrarSelectorTarjetas = true
                                        }
                                    }
                                    if(metodosPermitidos.contains("Transferencia")) MetodoPagoItem("Transfer", Icons.Default.AccountBalance, vm.metodoPago == "Transferencia", Modifier.weight(1f), isDark) { 
                                        vm.updateMetodoPago("Transferencia")
                                    }
                                }

                                AnimatedVisibility(
                                    visible = vm.metodoPago == "Transferencia",
                                    enter = slideInVertically(initialOffsetY = { it }) + expandVertically() + fadeIn(),
                                    exit = slideOutVertically(targetOffsetY = { it }) + shrinkVertically() + fadeOut()
                                ) {
                                    val configMaestra = ConfigGlobalRepository.configCargada
                                    val clabe = configMaestra?.clabe_maestra?.ifBlank { "000000000000000000" } ?: "000000000000000000"
                                    val clipboardManager = LocalClipboardManager.current
                                    var copiado by remember { mutableStateOf(false) }

                                    Card(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .padding(top = 12.dp),
                                        colors = CardDefaults.cardColors(containerColor = if (isDark) Color(0xFF0D253F) else Color(0xFFF0F7FF)),
                                        border = BorderStroke(1.dp, Color(0xFF1976D2).copy(alpha = 0.4f)),
                                        shape = RoundedCornerShape(16.dp)
                                    ) {
                                        Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(10.dp)) {
                                            Text("Pagar con Transferencia SPEI", fontWeight = FontWeight.ExtraBold, fontSize = 16.sp, color = colorTextoPrincipal)
                                            
                                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                                                Text("Monto exacto a transferir:", fontSize = 13.sp, color = colorTextoSecundario)
                                                Text("$${formatPrecio(vm.totalPagar)} MXN", fontWeight = FontWeight.Black, fontSize = 16.sp, color = RosaYendo)
                                            }

                                            HorizontalDivider(color = Color.Gray.copy(alpha = 0.2f))

                                            Text("Banco: ${configMaestra?.banco_maestro?.ifBlank { "STP / BBVA" } ?: "STP / BBVA"}", fontSize = 13.sp, fontWeight = FontWeight.Medium, color = colorTextoPrincipal)

                                            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth()) {
                                                Column(modifier = Modifier.weight(1f)) {
                                                    Text("CLABE Interbancaria:", fontSize = 11.sp, color = colorTextoSecundario)
                                                    Text(clabe, fontSize = 15.sp, fontWeight = FontWeight.Black, color = RosaYendo, letterSpacing = 1.sp)
                                                }
                                                IconButton(onClick = { 
                                                    clipboardManager.setText(AnnotatedString(clabe))
                                                    copiado = true
                                                }) {
                                                    Icon(if (copiado) Icons.Default.Check else Icons.Default.ContentCopy, null, tint = if (copiado) Color(0xFF4CAF50) else RosaYendo, modifier = Modifier.size(20.dp))
                                                }
                                            }
                                            if (copiado) {
                                                Text("¡CLABE copiada al portapapeles!", color = Color(0xFF4CAF50), fontSize = 11.sp, fontWeight = FontWeight.Bold)
                                            }

                                            Text("Beneficiario: ${configMaestra?.beneficiario_maestro?.ifBlank { "Yendo Logistics S.A. de C.V." } ?: "Yendo Logistics S.A. de C.V."}", fontSize = 12.sp, fontWeight = FontWeight.Medium, color = colorTextoPrincipal)

                                            Spacer(Modifier.height(4.dp))

                                            Button(
                                                onClick = {
                                                    esFotoPaquete = false
                                                    permissionManager.requestGalleryPermission { imagePicker.launch() }
                                                },
                                                modifier = Modifier.fillMaxWidth().height(48.dp),
                                                colors = ButtonDefaults.buttonColors(containerColor = if (imagenBytesComprobante != null) Color(0xFF4CAF50) else RosaYendo),
                                                shape = RoundedCornerShape(12.dp)
                                            ) {
                                                Icon(if (imagenBytesComprobante != null) Icons.Default.CheckCircle else Icons.Default.PhotoCamera, null, tint = Color.White)
                                                Spacer(Modifier.width(8.dp))
                                                Text(if (imagenBytesComprobante != null) "✓ Comprobante Adjuntado" else "Adjuntar Comprobante SPEI *", fontWeight = FontWeight.Bold, fontSize = 13.sp)
                                            }
                                        }
                                    }
                                }

                                if (vm.metodoPago == "Efectivo") {
                                    Spacer(Modifier.height(16.dp))
                                    Text("¿Quién pagará el envío?", fontWeight = FontWeight.Bold, fontSize = 14.sp, color = colorTextoPrincipal)

                                    val textoRemitente = if (vm.rolEnvio == "ENVIANDO") "Yo pago (Remitente)" else "Paga quien envía (Remitente)"
                                    val textoReceptor = if (vm.rolEnvio == "ENVIANDO") "Paga quien recibe (Receptor)" else "Yo pago (Receptor)"

                                    Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.clickable { vm.updateQuienPagaEfectivo("REMITENTE") }) {
                                        RadioButton(selected = vm.quienPagaEfectivo == "REMITENTE", onClick = { vm.updateQuienPagaEfectivo("REMITENTE") }, colors = RadioButtonDefaults.colors(selectedColor = RosaYendo))
                                        Text(textoRemitente, fontSize = 13.sp, color = colorTextoPrincipal)
                                    }
                                    Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.clickable { vm.updateQuienPagaEfectivo("RECEPTOR") }) {
                                        RadioButton(selected = vm.quienPagaEfectivo == "RECEPTOR", onClick = { vm.updateQuienPagaEfectivo("RECEPTOR") }, colors = RadioButtonDefaults.colors(selectedColor = RosaYendo))
                                        Text(textoReceptor, fontSize = 13.sp, color = colorTextoPrincipal)
                                    }
                                    Spacer(Modifier.height(12.dp))
                                    OpcionesPagoEfectivo(vm.totalPagar, vm.cambioEfectivoSeleccionado, isDark) { vm.updateCambioEfectivoSeleccionado(it) }
                                }

                                Spacer(Modifier.height(16.dp))
                                Row(modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                                    OutlinedTextField(
                                        value = vm.cuponInput,
                                        onValueChange = { vm.updateCuponInput(it.uppercase()) },
                                        placeholder = { Text("Cupón de descuento", color = colorTextoSecundario) },
                                        modifier = Modifier.weight(1f).height(56.dp),
                                        shape = RoundedCornerShape(12.dp),
                                        colors = OutlinedTextFieldDefaults.colors(focusedBorderColor = RosaYendo, unfocusedBorderColor = if(isDark) Color(0xFF3A3A3C) else Color(0xFFE0E0E0), focusedTextColor = colorTextoPrincipal, unfocusedTextColor = colorTextoPrincipal),
                                        singleLine = true
                                    )
                                    Spacer(Modifier.width(12.dp))
                                    Button(
                                        onClick = { vm.aplicarCupon() }, modifier = Modifier.height(56.dp),
                                        colors = ButtonDefaults.buttonColors(if(isDark) Color(0xFF444444) else Color(0xFF333333)), shape = RoundedCornerShape(12.dp)
                                    ) { Text("Aplicar", fontWeight = FontWeight.Bold, color = Color.White) }
                                }
                                if (vm.mensajeCupon.isNotEmpty()) {
                                    Text(vm.mensajeCupon, color = if (vm.descuentoAplicado > 0) Color(0xFF00C853) else Color.Red, fontSize = 12.sp, modifier = Modifier.padding(top = 8.dp, start = 4.dp))
                                }
                            }
                            } else {
                                item {
                                    Card(
                                        modifier = Modifier.fillMaxWidth(),
                                        colors = CardDefaults.cardColors(containerColor = colorCard.copy(alpha = 0.6f)),
                                        shape = RoundedCornerShape(16.dp),
                                        border = BorderStroke(1.dp, colorTextoSecundario.copy(alpha = 0.15f))
                                    ) {
                                        Row(
                                            modifier = Modifier.padding(16.dp),
                                            verticalAlignment = Alignment.CenterVertically
                                        ) {
                                            Icon(Icons.Default.Lock, contentDescription = "Bloqueado", tint = RosaYendo, modifier = Modifier.size(24.dp))
                                            Spacer(modifier = Modifier.width(12.dp))
                                            Text(
                                                "Selecciona el origen (Punto A) y destino (Punto B) para calcular tu tarifa y habilitar las opciones de pago.",
                                                fontSize = 13.sp,
                                                color = colorTextoSecundario,
                                                lineHeight = 18.sp
                                            )
                                        }
                                    }
                                }
                            }

                            item {
                                Spacer(Modifier.height(24.dp))
                                HorizontalDivider(color = colorTextoSecundario.copy(alpha=0.2f))
                                Spacer(Modifier.height(16.dp))

                                Text("Pautas para tu envío", fontWeight = FontWeight.Bold, fontSize = 16.sp, color = colorTextoPrincipal)
                                Spacer(Modifier.height(8.dp))
                                Text("Para una recolección y entrega exitosa, tu paquete debe:", color = colorTextoPrincipal, fontSize = 14.sp)
                                Text("• Estar bien sellado y listo para llevar.\n• No exceder los $2,000 MXN en valor.\n• Caber en el vehículo seleccionado.", color = colorTextoSecundario, fontSize = 13.sp, modifier = Modifier.padding(vertical = 8.dp))

                                Spacer(Modifier.height(12.dp))
                                Text("Artículos prohibidos", fontWeight = FontWeight.Bold, fontSize = 14.sp, color = colorTextoPrincipal)
                                Text("Está prohibido enviar alcohol, medicamentos, drogas, armas, dinero en efectivo, animales, artículos de valor sentimental irreemplazable, y materiales peligrosos o ilegales.",
                                    color = colorTextoSecundario, fontSize = 12.sp, modifier = Modifier.padding(top = 4.dp, bottom=4.dp), lineHeight = 16.sp)

                                OutlinedButton(
                                    onClick = { uriHandler.openUri(ConfigGlobalRepository.configCargada?.url_articulos_prohibidos ?: "https://www.yendo.com.mx") },
                                    modifier = Modifier.fillMaxWidth(),
                                    shape = RoundedCornerShape(12.dp),
                                    border = BorderStroke(1.dp, colorTextoSecundario.copy(alpha = 0.5f))
                                ) {
                                    Text("Ver lista completa de artículos prohibidos", color = RosaYendo, fontWeight = FontWeight.Bold, fontSize = 12.sp)
                                }

                                Spacer(Modifier.height(16.dp))

                                Card(
                                    onClick = { uriHandler.openUri(ConfigGlobalRepository.configCargada?.url_terminos ?: "https://www.yendo.com.mx") },
                                    colors = CardDefaults.cardColors(containerColor = if(isDark) Color(0xFF2C2C2E) else Color(0xFFF5F5F5)),
                                    shape = RoundedCornerShape(12.dp)
                                ) {
                                    Text("Al presionar el botón de Confirmar Envío, estás aceptando que tu paquete cumple con estas pautas y los Términos y Condiciones de Yendo Mx.",
                                        color = colorTextoSecundario, fontSize = 11.sp, textAlign = TextAlign.Center, modifier = Modifier.padding(12.dp))
                                }
                            }

                    vm.error?.let { item { Text(it, color = Color.Red, fontSize = 14.sp, fontWeight = FontWeight.Bold, modifier = Modifier.padding(top=16.dp)) } }
                }
            }
        }

    if (mostrarSelectorTarjetas) {
            ModalBottomSheet(
                onDismissRequest = { mostrarSelectorTarjetas = false },
                containerColor = colorCard, shape = RoundedCornerShape(topStart = 24.dp, topEnd = 24.dp)
            ) {
                Column(modifier = Modifier.fillMaxWidth().padding(horizontal = 20.dp, vertical = 8.dp)) {
                    Text("Selecciona una tarjeta", fontWeight = FontWeight.ExtraBold, fontSize = 20.sp, color = colorTextoPrincipal, modifier = Modifier.padding(bottom = 16.dp))

                    LazyRow(horizontalArrangement = Arrangement.spacedBy(12.dp), contentPadding = PaddingValues(bottom = 16.dp)) {
                        items(tarjetasGuardadas) { tarjeta ->
                            val isSelected = tarjetaSeleccionada?.id == tarjeta.id
                            val (marcaColor, marcaBg) = when(tarjeta.marca.lowercase()) {
                                "visa" -> Pair(Color.White, Color(0xFF1A237E))
                                "mastercard" -> Pair(Color.White, Color(0xFFB71C1C))
                                "amex" -> Pair(Color.White, Color(0xFF00695C))
                                else -> Pair(Color.White, Color.DarkGray)
                            }

                            Card(
                                onClick = { tarjetaSeleccionada = tarjeta; mostrarSelectorTarjetas = false },
                                modifier = Modifier.width(180.dp).height(100.dp),
                                colors = CardDefaults.cardColors(containerColor = if (isSelected) RosaYendo else colorFondoGlobal),
                                border = BorderStroke(1.dp, if (isSelected) RosaYendo else if(isDark) Color(0xFF2C2C2E) else Color(0xFFEEEEEE)),
                                shape = RoundedCornerShape(16.dp)
                            ) {
                                Column(modifier = Modifier.padding(12.dp).fillMaxSize(), verticalArrangement = Arrangement.SpaceBetween) {
                                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                                        Box(modifier = Modifier.clip(RoundedCornerShape(4.dp)).background(if (isSelected) Color.White.copy(alpha = 0.2f) else marcaBg).padding(horizontal = 6.dp, vertical = 2.dp)) {
                                            Text(tarjeta.marca, color = if (isSelected) Color.White else marcaColor, fontSize = 10.sp, fontWeight = FontWeight.Black)
                                        }
                                        if (isSelected) Icon(Icons.Default.CheckCircle, null, tint = Color.White, modifier = Modifier.size(20.dp))
                                    }
                                    Text("•••• ${tarjeta.terminacion}", color = if (isSelected) Color.White else colorTextoPrincipal, fontWeight = FontWeight.Bold, fontSize = 16.sp, letterSpacing = 2.sp)
                                }
                            }
                        }
                    }

                    HorizontalDivider(color = if(isDark) Color(0xFF2C2C2E) else Color(0xFFEEEEEE), modifier = Modifier.padding(vertical = 12.dp))

                    Row(
                        modifier = Modifier.fillMaxWidth().clip(RoundedCornerShape(12.dp)).clickable { mostrarDialogoAgregarTarjeta = true }.padding(vertical = 16.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Box(modifier = Modifier.size(40.dp).background(RosaYendo.copy(alpha = 0.1f), CircleShape), contentAlignment = Alignment.Center) { Icon(Icons.Default.Add, null, tint = RosaYendo) }
                        Spacer(Modifier.width(16.dp))
                        Text("Agregar nueva tarjeta", fontWeight = FontWeight.Bold, color = RosaYendo, fontSize = 16.sp)
                    }
                    Spacer(Modifier.height(30.dp).navigationBarsPadding())
                }
            }
        }

        if (mostrarDialogoAgregarTarjeta) {
            com.yendo.mx.ui.profile.DialogoNuevaTarjeta(
                isDark = isDark,
                onDismiss = { mostrarDialogoAgregarTarjeta = false },
                onGuardar = { nuevaTarjeta ->
                    scope.launch {
                        try {
                            val docRef = Firebase.firestore.collection("Usuarios").document(usuarioId!!).collection("Tarjetas").document
                            val tarjetaConId = nuevaTarjeta.copy(id = docRef.id)
                            docRef.set(tarjetaConId)

                            tarjetasGuardadas.add(tarjetaConId)
                            tarjetaSeleccionada = tarjetaConId
                            mostrarDialogoAgregarTarjeta = false
                            mostrarSelectorTarjetas = false
                        } catch (e: Exception) {
                            println("YENDO_ERROR: Falló al guardar tarjeta - ${e.message}")
                        }
                    }
                }
            )
        }

        if (vm.pedidoExitosoId != null) {
            val idPedido = vm.pedidoExitosoId!!
            AlertDialog(
                onDismissRequest = { vm.pedidoExitosoId = null; vm.limpiarFormulario(); onPedidoConfirmado(idPedido) },
                confirmButton = { Button(onClick = { vm.pedidoExitosoId = null; vm.limpiarFormulario(); onPedidoConfirmado(idPedido) }, colors = ButtonDefaults.buttonColors(RosaYendo)) { Text("Entendido", color = Color.White) } },
                title = { Text("¡Envío Solicitado!", color = colorTextoPrincipal) },
                text = { Text("Tu pedido ha sido inyectado con éxito. Te avisaremos cuando un repartidor acepte el viaje.", color = colorTextoSecundario) },
                containerColor = colorCard
            )
        }

        if (vm.mostrarDialogoCVV) {
            AlertDialog(
                onDismissRequest = { vm.mostrarDialogoCVV = false },
                title = { Text("Verificación de Seguridad", color = colorTextoPrincipal) },
                text = {
                    Column {
                        Text("Ingresa el código de seguridad de tu tarjeta para confirmar el pago.", fontSize = 14.sp, color = colorTextoSecundario, modifier = Modifier.padding(bottom = 16.dp))
                        OutlinedTextField(
                            value = vm.cvvInput,
                            onValueChange = { if(it.length <= 4 && it.all { char -> char.isDigit() }) vm.cvvInput = it },
                            label = { Text("CVV") },
                            placeholder = { Text("3 o 4 dígitos") },
                            modifier = Modifier.fillMaxWidth(),
                            keyboardOptions = androidx.compose.foundation.text.KeyboardOptions(keyboardType = KeyboardType.NumberPassword),
                            colors = OutlinedTextFieldDefaults.colors(focusedBorderColor = RosaYendo, focusedTextColor = colorTextoPrincipal, unfocusedTextColor = colorTextoPrincipal)
                        )
                    }
                },
                confirmButton = {
                    Button(
                        onClick = { vm.procesarPagoConCVV(tarjetaSeleccionada!!.tokenPago, tarjetaSeleccionada!!.issuerId) },
                        colors = ButtonDefaults.buttonColors(containerColor = RosaYendo),
                        enabled = vm.cvvInput.length >= 3
                    ) { Text("Confirmar", color = Color.White) }
                },
                dismissButton = {
                    TextButton(onClick = { vm.mostrarDialogoCVV = false }) { Text("Cancelar", color = RosaYendo) }
                },
                containerColor = colorCard
            )
        }

        if (mostrarOpcionesFoto) {
            ModalBottomSheet(
                onDismissRequest = { mostrarOpcionesFoto = false },
                containerColor = colorCard,
                shape = RoundedCornerShape(topStart = 24.dp, topEnd = 24.dp)
            ) {
                Column(modifier = Modifier.fillMaxWidth().padding(horizontal = 20.dp, vertical = 24.dp)) {
                    Text("Seleccionar Foto", fontWeight = FontWeight.Black, fontSize = 20.sp, color = colorTextoPrincipal, modifier = Modifier.padding(bottom = 16.dp))
                    
                    Row(
                        modifier = Modifier.fillMaxWidth().clickable { 
                            mostrarOpcionesFoto = false
                            esFotoPaquete = true
                            permissionManager.requestCameraPermission()
                        }.padding(vertical = 12.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Icon(Icons.Default.PhotoCamera, null, tint = RosaYendo, modifier = Modifier.size(28.dp))
                        Spacer(Modifier.width(16.dp))
                        Text("Tomar foto ahora", fontWeight = FontWeight.Bold, fontSize = 16.sp, color = colorTextoPrincipal)
                    }
                    
                    Row(
                        modifier = Modifier.fillMaxWidth().clickable { 
                            mostrarOpcionesFoto = false
                            esFotoPaquete = true
                            permissionManager.requestGalleryPermission { imagePicker.launch() }
                        }.padding(vertical = 12.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Icon(Icons.Default.PhotoLibrary, null, tint = RosaYendo, modifier = Modifier.size(28.dp))
                        Spacer(Modifier.width(16.dp))
                        Text("Elegir de la galería", fontWeight = FontWeight.Bold, fontSize = 16.sp, color = colorTextoPrincipal)
                    }
                    
                    Spacer(Modifier.height(32.dp).navigationBarsPadding())
                }
            }
        }

        if (mostrarSheetTransferencia) {
            val configMaestra = ConfigGlobalRepository.configCargada
            val clabe = configMaestra?.clabe_maestra?.ifBlank { "000000000000000000" } ?: "000000000000000000"
            val clipboardManager = LocalClipboardManager.current
            var copiado by remember { mutableStateOf(false) }

            ModalBottomSheet(
                onDismissRequest = { mostrarSheetTransferencia = false },
                containerColor = colorCard,
                shape = RoundedCornerShape(topStart = 24.dp, topEnd = 24.dp)
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 24.dp, vertical = 12.dp),
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text("Detalles de Transferencia SPEI 🏦", fontWeight = FontWeight.ExtraBold, fontSize = 20.sp, color = colorTextoPrincipal)
                        IconButton(onClick = { mostrarSheetTransferencia = false }) {
                            Icon(Icons.Default.Close, contentDescription = "Cerrar", tint = colorTextoSecundario)
                        }
                    }

                    Surface(
                        color = if (isDark) Color(0xFF0D253F) else Color(0xFFF0F7FF),
                        shape = RoundedCornerShape(16.dp),
                        border = BorderStroke(1.dp, Color(0xFF1976D2).copy(alpha = 0.3f))
                    ) {
                        Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(10.dp)) {
                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                                Text("Monto exacto a transferir:", fontSize = 13.sp, color = colorTextoSecundario)
                                Text("$${formatPrecio(vm.totalPagar)} MXN", fontWeight = FontWeight.Black, fontSize = 18.sp, color = RosaYendo)
                            }

                            HorizontalDivider(color = Color.Gray.copy(alpha = 0.2f))

                            Text("Banco: ${configMaestra?.banco_maestro?.ifBlank { "STP / BBVA" } ?: "STP / BBVA"}", fontSize = 14.sp, fontWeight = FontWeight.Medium, color = colorTextoPrincipal)

                            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth()) {
                                Column(modifier = Modifier.weight(1f)) {
                                    Text("CLABE Interbancaria:", fontSize = 11.sp, color = colorTextoSecundario)
                                    Text(clabe, fontSize = 16.sp, fontWeight = FontWeight.Black, color = RosaYendo, letterSpacing = 1.sp)
                                }
                                IconButton(onClick = { 
                                    clipboardManager.setText(AnnotatedString(clabe))
                                    copiado = true
                                }) {
                                    Icon(if (copiado) Icons.Default.Check else Icons.Default.ContentCopy, null, tint = if (copiado) Color(0xFF4CAF50) else RosaYendo, modifier = Modifier.size(22.dp))
                                }
                            }
                            if (copiado) {
                                Text("¡CLABE copiada al portapapeles!", color = Color(0xFF4CAF50), fontSize = 12.sp, fontWeight = FontWeight.Bold)
                            }

                            Text("Beneficiario: ${configMaestra?.beneficiario_maestro?.ifBlank { "Yendo Logistics S.A. de C.V." } ?: "Yendo Logistics S.A. de C.V."}", fontSize = 13.sp, fontWeight = FontWeight.Medium, color = colorTextoPrincipal)
                        }
                    }

                    Button(
                        onClick = {
                            esFotoPaquete = false
                            permissionManager.requestGalleryPermission { imagePicker.launch() }
                        },
                        modifier = Modifier.fillMaxWidth().height(52.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = if (imagenBytesComprobante != null) Color(0xFF4CAF50) else RosaYendo),
                        shape = RoundedCornerShape(12.dp)
                    ) {
                        Icon(if (imagenBytesComprobante != null) Icons.Default.CheckCircle else Icons.Default.PhotoCamera, null, tint = Color.White)
                        Spacer(Modifier.width(8.dp))
                        Text(if (imagenBytesComprobante != null) "✓ Comprobante Adjuntado" else "📸 Adjuntar Comprobante SPEI *", fontWeight = FontWeight.Bold, fontSize = 15.sp)
                    }

                    Button(
                        onClick = { mostrarSheetTransferencia = false },
                        modifier = Modifier.fillMaxWidth().height(48.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = if (isDark) Color(0xFF333333) else Color(0xFFE0E0E0)),
                        shape = RoundedCornerShape(12.dp)
                    ) {
                        Text("Listo", fontWeight = FontWeight.Bold, color = colorTextoPrincipal)
                    }

                    Spacer(Modifier.navigationBarsPadding().height(16.dp))
                }
            }
        }
    }



@Composable
fun UbicacionRow(
    icon: ImageVector,
    titulo: String,
    subtitulo: String,
    colorIcono: Color,
    colorTextoPrincipal: Color,
    onClick: () -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable { onClick() }
            .padding(vertical = 8.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(icon, null, tint = colorIcono, modifier = Modifier.size(24.dp))
        Spacer(Modifier.width(16.dp))
        Column(modifier = Modifier.weight(1f)) {
            Text(titulo, fontSize = 12.sp, color = Color.Gray)
            Text(subtitulo, fontSize = 14.sp, fontWeight = FontWeight.Bold, color = colorTextoPrincipal, maxLines = 1, overflow = TextOverflow.Ellipsis)
        }
        Icon(Icons.Default.ChevronRight, null, tint = Color.Gray, modifier = Modifier.size(20.dp))
    }
}

@Composable
fun EmergencyBlockedScreen(
    mensaje: String,
    colorTextoPrincipal: Color,
    colorTextoSecundario: Color,
    onBack: () -> Unit
) {
    Box(modifier = Modifier.fillMaxSize().background(if (isSystemInDarkTheme()) Color(0xFF121212) else Color(0xFFF8F8F8))) {
        Column(
            modifier = Modifier.fillMaxSize().padding(32.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            Box(
                modifier = Modifier.size(80.dp).background(RosaYendo.copy(alpha = 0.1f), CircleShape),
                contentAlignment = Alignment.Center
            ) {
                Icon(Icons.Default.Warning, null, tint = RosaYendo, modifier = Modifier.size(40.dp))
            }
            Spacer(Modifier.height(24.dp))
            Text(
                "Servicio Pausado",
                fontWeight = FontWeight.ExtraBold,
                fontSize = 22.sp,
                color = colorTextoPrincipal,
                textAlign = TextAlign.Center
            )
            Spacer(Modifier.height(12.dp))
            Text(
                mensaje,
                fontSize = 15.sp,
                color = colorTextoSecundario,
                textAlign = TextAlign.Center,
                lineHeight = 22.sp
            )
            Spacer(Modifier.height(32.dp))
            Button(
                onClick = onBack,
                colors = ButtonDefaults.buttonColors(containerColor = RosaYendo),
                shape = RoundedCornerShape(12.dp),
                modifier = Modifier.fillMaxWidth().height(52.dp)
            ) {
                Text("Entendido", color = Color.White, fontWeight = FontWeight.Bold)
            }
        }
    }
}
